import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Facebook, Instagram, Phone, MapPin, Clock, Wine, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// House/Cottage icon EXACTLY matching the original design - dark wine color
const HouseIcon = ({ className = "w-16 h-16", color = "#5D2E35" }) => (
  <svg viewBox="0 0 80 90" className={className} fill="none">
    {/* Chimney smoke - wavy lines */}
    <path d="M52 5C52 5 54 0 52 -4" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.6"/>
    <path d="M57 3C57 3 60 -3 57 -8" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.6"/>
    {/* Chimney */}
    <rect x="47" y="10" width="14" height="22" fill={color}/>
    {/* Main roof - steep pointed roof */}
    <path d="M40 18L8 52H72L40 18Z" fill={color}/>
    {/* Roof top decoration/peak */}
    <path d="M40 18L40 8" stroke={color} strokeWidth="3"/>
    <circle cx="40" cy="6" r="3" fill={color}/>
    {/* House body */}
    <rect x="15" y="52" width="50" height="35" fill={color}/>
    {/* Door - arched top */}
    <path d="M32 87V65C32 60 36 57 40 57C44 57 48 60 48 65V87" fill={color} stroke="#F5EDE0" strokeWidth="2"/>
    {/* Door inner arch */}
    <path d="M35 87V68C35 64 37 62 40 62C43 62 45 64 45 68V87" fill="#F5EDE0" opacity="0.15"/>
    {/* Left window */}
    <rect x="20" y="60" width="8" height="10" rx="1" fill={color} stroke="#F5EDE0" strokeWidth="1.5"/>
    <line x1="24" y1="60" x2="24" y2="70" stroke="#F5EDE0" strokeWidth="1" opacity="0.5"/>
    <line x1="20" y1="65" x2="28" y2="65" stroke="#F5EDE0" strokeWidth="1" opacity="0.5"/>
    {/* Right window */}
    <rect x="52" y="60" width="8" height="10" rx="1" fill={color} stroke="#F5EDE0" strokeWidth="1.5"/>
    <line x1="56" y1="60" x2="56" y2="70" stroke="#F5EDE0" strokeWidth="1" opacity="0.5"/>
    <line x1="52" y1="65" x2="60" y2="65" stroke="#F5EDE0" strokeWidth="1" opacity="0.5"/>
  </svg>
);

// Icons matching the original golden style
const LightbulbIcon = () => (
  <svg viewBox="0 0 80 80" className="w-16 h-16">
    <circle cx="40" cy="40" r="35" fill="#DAA520" fillOpacity="0.15"/>
    <path d="M40 15C30 15 22 23 22 33C22 40 26 46 32 49V56C32 58 34 60 36 60H44C46 60 48 58 48 56V49C54 46 58 40 58 33C58 23 50 15 40 15Z" 
          fill="none" stroke="#DAA520" strokeWidth="2.5"/>
    <path d="M35 64H45" stroke="#DAA520" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M37 68H43" stroke="#DAA520" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M40 25V35" stroke="#DAA520" strokeWidth="2" strokeLinecap="round"/>
    <path d="M35 30L40 35L45 30" stroke="#DAA520" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <circle cx="32" cy="22" r="2" fill="#DAA520" fillOpacity="0.6"/>
    <circle cx="48" cy="22" r="1.5" fill="#DAA520" fillOpacity="0.6"/>
  </svg>
);

const CrystalBallIcon = () => (
  <svg viewBox="0 0 80 80" className="w-16 h-16">
    <circle cx="40" cy="40" r="35" fill="#DAA520" fillOpacity="0.15"/>
    <circle cx="40" cy="35" r="20" fill="none" stroke="#DAA520" strokeWidth="2.5"/>
    <ellipse cx="40" cy="60" rx="18" ry="5" fill="none" stroke="#DAA520" strokeWidth="2"/>
    <path d="M22 55C22 55 28 52 40 52C52 52 58 55 58 55" stroke="#DAA520" strokeWidth="2" fill="none"/>
    <path d="M32 28C34 26 38 26 40 28" stroke="#DAA520" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <circle cx="32" cy="18" r="2" fill="#DAA520" fillOpacity="0.6"/>
    <circle cx="50" cy="20" r="1.5" fill="#DAA520" fillOpacity="0.6"/>
  </svg>
);

const BarrelIcon = () => (
  <svg viewBox="0 0 80 80" className="w-16 h-16">
    <circle cx="40" cy="40" r="35" fill="#DAA520" fillOpacity="0.15"/>
    <rect x="15" y="25" width="22" height="30" rx="3" fill="none" stroke="#DAA520" strokeWidth="2"/>
    <rect x="43" y="25" width="22" height="30" rx="3" fill="none" stroke="#DAA520" strokeWidth="2"/>
    <line x1="15" y1="35" x2="37" y2="35" stroke="#DAA520" strokeWidth="1.5"/>
    <line x1="15" y1="45" x2="37" y2="45" stroke="#DAA520" strokeWidth="1.5"/>
    <line x1="43" y1="35" x2="65" y2="35" stroke="#DAA520" strokeWidth="1.5"/>
    <line x1="43" y1="45" x2="65" y2="45" stroke="#DAA520" strokeWidth="1.5"/>
    <ellipse cx="26" cy="25" rx="8" ry="3" fill="none" stroke="#DAA520" strokeWidth="1.5"/>
    <ellipse cx="54" cy="25" rx="8" ry="3" fill="none" stroke="#DAA520" strokeWidth="1.5"/>
  </svg>
);

const CoinsHandIcon = () => (
  <svg viewBox="0 0 80 80" className="w-16 h-16">
    <circle cx="40" cy="40" r="35" fill="#DAA520" fillOpacity="0.15"/>
    <circle cx="40" cy="30" r="14" fill="none" stroke="#DAA520" strokeWidth="2.5"/>
    <text x="40" y="35" textAnchor="middle" fill="#DAA520" fontSize="14" fontWeight="bold">$</text>
    <path d="M20 55C20 55 25 48 40 48C55 48 60 55 60 55" stroke="#DAA520" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M25 58L20 65" stroke="#DAA520" strokeWidth="2" strokeLinecap="round"/>
    <path d="M55 58L60 65" stroke="#DAA520" strokeWidth="2" strokeLinecap="round"/>
    <path d="M40 52V62" stroke="#DAA520" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="30" cy="18" r="2" fill="#DAA520" fillOpacity="0.6"/>
    <circle cx="52" cy="20" r="1.5" fill="#DAA520" fillOpacity="0.6"/>
  </svg>
);

const LandingPage = () => {
  const [content, setContent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    interest_type: "",
    phone: "",
    region: "",
    wine_types: "",
    production_capacity: "",
    company_name: "",
    preferred_wines: "",
    purchase_frequency: "",
    accepted_privacy: false
  });
  const [loading, setLoading] = useState(false);
  const [showProducerFields, setShowProducerFields] = useState(false);
  const [showBuyerFields, setShowBuyerFields] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroImages = [
    'https://customer-assets.emergentagent.com/job_wine-marketplace-7/artifacts/6n0pqotb_imagem01.png',
    'https://customer-assets.emergentagent.com/job_wine-marketplace-7/artifacts/xlratjre_ChatGPT%20Image%208_02_2026%2C%2016_47_55.png'
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/content`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching content:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.interest_type) {
      toast.error("Por favor, preencha todos os campos obrigatórios");
      return;
    }
    
    if (!formData.accepted_privacy) {
      toast.error("Você precisa aceitar a Política de Privacidade");
      return;
    }

    setLoading(true);
    try {
      if (formData.interest_type === "produtor") {
        await axios.post(`${API_URL}/producers`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "Não informado",
          region: formData.region || "Não informado",
          wine_types: formData.wine_types || "Não informado",
          company_name: formData.company_name || "Não informado",
          production_capacity: formData.production_capacity || "Não informado",
          accepted_privacy: formData.accepted_privacy
        });
        toast.success("Cadastro de produtor realizado com sucesso!");
      } else {
        await axios.post(`${API_URL}/leads`, {
          name: formData.name,
          email: formData.email,
          interest_type: formData.interest_type,
          phone: formData.phone || "Não informado",
          region: formData.region || "Não informado",
          preferred_wines: formData.preferred_wines || "Não informado",
          purchase_frequency: formData.purchase_frequency || "Não informado",
          accepted_privacy: formData.accepted_privacy
        });
        toast.success("Cadastro realizado com sucesso!");
      }
      setFormData({
        name: "",
        email: "",
        interest_type: "",
        phone: "",
        region: "",
        wine_types: "",
        production_capacity: "",
        company_name: "",
        preferred_wines: "",
        purchase_frequency: "",
        accepted_privacy: false
      });
      setShowProducerFields(false);
      setShowBuyerFields(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao realizar cadastro.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("register-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToAbout = () => {
    document.getElementById("about-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToProducer = () => {
    document.getElementById("producer-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToBenefits = () => {
    document.getElementById("benefits-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Lato', sans-serif" }}>
      {/* Top Menu/Navbar */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_wine-marketplace-7/artifacts/q5fe9prt_logo1_ocaseirinho.png"
                alt="O Caseirinho"
                className="h-16 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button 
                onClick={scrollToAbout}
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium"
              >
                Sobre
              </button>
              <Link 
                to="/produtores"
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium"
              >
                Produtores
              </Link>
              <Link 
                to="/beneficios"
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium"
              >
                Benefícios
              </Link>
              <button 
                onClick={scrollToForm}
                className="px-5 py-2 rounded text-sm font-medium transition-colors"
                style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF'
                }}
              >
                Cadastre-se
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#722F37] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col gap-3">
                <button 
                  onClick={() => { scrollToAbout(); setMobileMenuOpen(false); }}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2"
                >
                  Sobre
                </button>
                <Link 
                  to="/produtores"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2"
                >
                  Produtores
                </Link>
                <Link 
                  to="/beneficios"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2"
                >
                  Benefícios
                </Link>
                <button 
                  onClick={() => { scrollToForm(); setMobileMenuOpen(false); }}
                  className="px-5 py-2 rounded text-sm font-medium transition-colors text-center mt-2"
                  style={{
                    backgroundColor: '#722F37',
                    color: '#FFFFFF'
                  }}
                >
                  Cadastre-se
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Slider with multiple images */}
      <section 
        className="relative min-h-[620px] flex flex-col items-center justify-center pt-16 overflow-hidden"
      >
        {/* Background Images with Fade Transition */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${image}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: currentSlide === index ? 1 : 0
            }}
          ></div>
        ))}
        
        {/* Subtle overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)'
          }}
        ></div>
        
        <div className="relative z-10 text-center px-6 md:px-4 max-w-4xl mx-auto flex flex-col items-center">
          {/* Main headline - Bold serif */}
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-2 leading-tight md:leading-relaxed"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              color: '#F5EDE0',
              textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
            }}
            data-testid="hero-title"
          >
            {content?.hero_title || "O Caseirinho – Vinhos artesanais direto do produtor para você"}
          </h2>
          
          {/* Subtitle */}
          <p 
            className="text-sm sm:text-base md:text-lg mb-5 md:mb-4 px-2 md:px-0"
            style={{ 
              fontFamily: "'Lato', sans-serif",
              color: '#F5EDE0',
              fontWeight: 400,
              textShadow: '1px 1px 3px rgba(0,0,0,0.4)'
            }}
            data-testid="hero-subtitle"
          >
            {content?.hero_subtitle || "Registre-se e seja o primeiro a saber quando nossa plataforma estiver aberta!"}
          </p>
          
          {/* CTA Button */}
          <button 
            onClick={scrollToForm}
            data-testid="hero-cta-btn"
            className="text-base sm:text-lg font-medium px-8 sm:px-10 py-3 sm:py-4 transition-all duration-300 hover:opacity-90"
            style={{
              backgroundColor: '#7A2E38',
              color: '#F5EDE0',
              border: '2px solid #5D2E35',
              borderRadius: '4px',
              fontFamily: "'Lato', sans-serif",
              letterSpacing: '0.5px'
            }}
          >
            Quero me cadastrar
          </button>
        </div>
        
        {/* Slide Indicators/Dots */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-white scale-110' 
                  : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* About Section - Wine background with image overlay */}
      <section 
        id="about-section"
        className="relative min-h-[400px] md:min-h-[450px] overflow-hidden"
        style={{
          backgroundColor: '#722F37'
        }}
      >
        {/* Wine bottles image on the right with fade effect */}
        <div 
          className="absolute right-0 top-0 bottom-8 w-[45%] hidden md:block"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_728153dc-96ca-4c27-a962-b3dd550d3ec5/artifacts/kb4oajh6_vinhos.png')`,
            backgroundSize: "cover",
            backgroundPosition: "top center",
            backgroundRepeat: "no-repeat",
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 10%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,1) 50%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 10%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,1) 50%)',
            imageRendering: 'high-quality'
          }}
        ></div>
        
        {/* White/cream bar at bottom */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: '#F5EEE0'
          }}
        ></div>
        
        <div className="absolute inset-0 flex items-center pt-16 relative z-10">
          <div className="max-w-5xl mx-auto px-8 md:px-16 w-full">
            <div className="max-w-lg">
              {/* Title with underline */}
              <h2 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
                data-testid="about-title"
              >
                {content?.about_title || "Sobre o Caseirinho"}
              </h2>
              <div 
                className="w-48 h-[2px] mb-6"
                style={{ backgroundColor: 'rgba(245, 237, 224, 0.5)' }}
              ></div>
              
              {/* Text */}
              <p 
                className="leading-relaxed text-lg md:text-xl mb-4"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: 'italic',
                  color: '#F5EDE0'
                }}
                data-testid="about-text"
              >
                {content?.about_text || "O Caseirinho conecta apaixonados por vinho a produtores artesanais de todo o país. Comprando direto do produtor, você garante qualidade e tradição em cada garrafa."}
              </p>
            </div>
          </div>
        </div>
      </section>
          
      {/* Producer CTA Section - Recreated design */}
      <section 
        id="producer-section"
        className="relative py-12 md:py-16"
        style={{
          backgroundColor: '#F5EEE0',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%239C6644' fill-opacity='0.02'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z'/%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        <div className="max-w-5xl mx-auto px-8 md:px-16 relative z-10">
          {/* Title */}
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              color: '#722F37'
            }}
            data-testid="producer-title"
          >
            {content?.producer_title || "É Produtor?"}
          </h2>
          
          {/* Decorative line */}
          <div 
            className="w-full h-[2px] mb-8"
            style={{ 
              background: 'linear-gradient(to right, rgba(114, 47, 55, 0.3), rgba(114, 47, 55, 0.1), transparent)'
            }}
          ></div>
          
          {/* Content row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Text */}
            <p 
              className="text-lg md:text-xl max-w-md"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
                color: '#722F37'
              }}
              data-testid="producer-text"
            >
              {content?.producer_text || "Cadastre-se e faça parte da nossa rede de produtores de vinhos artesanais!"}
            </p>
            
            {/* Styled button - Professional design */}
            <button 
              onClick={() => {
                setFormData(prev => ({ ...prev, interest_type: "produtor" }));
                setShowProducerFields(true);
                setShowBuyerFields(false);
                scrollToForm();
              }}
              data-testid="producer-cta-btn"
              className="relative px-10 py-4 text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: '#F5EDE0',
                backgroundColor: '#722F37',
                border: '2px solid #722F37',
                borderRadius: '4px',
                boxShadow: '0 4px 15px rgba(114, 47, 55, 0.3)',
                letterSpacing: '0.5px'
              }}
            >
              <span className="relative z-10">Quero ser parceiro</span>
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(to right, #5D2E35, #722F37, #5D2E35)',
                  borderRadius: '2px'
                }}
              ></div>
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section - Wine background */}
      <section 
        id="benefits-section"
        className="py-12 md:py-16"
        style={{
          backgroundColor: '#722F37'
        }}
      >
        <div className="max-w-6xl mx-auto px-4">
          {/* Title with decorative lines */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <div 
              className="h-[1px] w-24 md:w-40"
              style={{ background: 'linear-gradient(to right, transparent, #D4AF37)' }}
            ></div>
            <h2 
              className="text-xl md:text-2xl font-bold whitespace-nowrap"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: '#F5EDE0'
              }}
            >
              Por que se cadastrar?
            </h2>
            <div 
              className="h-[1px] w-24 md:w-40"
              style={{ background: 'linear-gradient(to left, transparent, #D4AF37)' }}
            ></div>
          </div>
          
          {/* Benefits Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div className="text-center" data-testid="benefit-card-0">
              <div className="flex justify-center mb-3">
                <LightbulbIcon />
              </div>
              <h3 
                className="text-sm md:text-base font-semibold"
                style={{ fontFamily: "'Playfair Display', serif", color: '#F5EDE0' }}
              >
                Novidades exclusivas
              </h3>
            </div>
            
            <div className="text-center" data-testid="benefit-card-1">
              <div className="flex justify-center mb-3">
                <CrystalBallIcon />
              </div>
              <h3 
                className="text-sm md:text-base font-semibold"
                style={{ fontFamily: "'Playfair Display', serif", color: '#F5EDE0' }}
              >
                Seja avisado da abertura
              </h3>
            </div>
            
            <div className="text-center" data-testid="benefit-card-2">
              <div className="flex justify-center mb-3">
                <BarrelIcon />
              </div>
              <h3 
                className="text-sm md:text-base font-semibold"
                style={{ fontFamily: "'Playfair Display', serif", color: '#F5EDE0' }}
              >
                Conheça produtores locais
              </h3>
            </div>
            
            <div className="text-center" data-testid="benefit-card-3">
              <div className="flex justify-center mb-3">
                <CoinsHandIcon />
              </div>
              <h3 
                className="text-sm md:text-base font-semibold"
                style={{ fontFamily: "'Playfair Display', serif", color: '#F5EDE0' }}
              >
                Ganhe comissão por indicações
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section 
        id="register-form" 
        className="py-12 md:py-16"
        style={{
          backgroundColor: '#F5EEE0',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%239C6644' fill-opacity='0.02'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z'/%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        <div className="max-w-xl mx-auto px-4">
          {/* Title with decorative lines */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <span style={{ color: '#722F37', fontSize: '20px' }}>•</span>
            <div 
              className="h-[2px] w-16 md:w-24"
              style={{ backgroundColor: '#722F37' }}
            ></div>
            <h2 
              className="text-xl md:text-2xl font-bold whitespace-nowrap"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: '#722F37'
              }}
            >
              Registre-se agora
            </h2>
            <div 
              className="h-[2px] w-16 md:w-24"
              style={{ backgroundColor: '#722F37' }}
            ></div>
            <span style={{ color: '#722F37', fontSize: '20px' }}>•</span>
          </div>
          
          {/* Form */}
          <form 
            onSubmit={handleSubmit}
            className="p-6 md:p-8 rounded-xl shadow-xl"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D5' }}
            data-testid="registration-form"
          >
            <div className="space-y-5">
              {/* Interest Type Selection */}
              <div className="mb-6">
                <label 
                  className="block text-sm font-semibold mb-3"
                  style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
                >
                  Estou interessado em:
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <label 
                    className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      formData.interest_type === "comprar" 
                        ? 'border-[#722F37] bg-[#722F37]/5' 
                        : 'border-[#E8E0D5] hover:border-[#722F37]/50'
                    }`}
                  >
                    <Checkbox 
                      checked={formData.interest_type === "comprar"}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData(prev => ({ ...prev, interest_type: "comprar" }));
                          setShowProducerFields(false);
                          setShowBuyerFields(true);
                        }
                      }}
                      className="border-gray-400 data-[state=checked]:bg-[#722F37] data-[state=checked]:border-[#722F37]"
                      data-testid="checkbox-comprar"
                    />
                    <div>
                      <span className="font-medium" style={{ color: '#5D4037' }}>Comprar Vinhos</span>
                      <p className="text-xs text-gray-500 mt-0.5">Quero descobrir vinhos artesanais</p>
                    </div>
                  </label>
                  <label 
                    className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      formData.interest_type === "produtor" 
                        ? 'border-[#722F37] bg-[#722F37]/5' 
                        : 'border-[#E8E0D5] hover:border-[#722F37]/50'
                    }`}
                  >
                    <Checkbox 
                      checked={formData.interest_type === "produtor"}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData(prev => ({ ...prev, interest_type: "produtor" }));
                          setShowProducerFields(true);
                          setShowBuyerFields(false);
                        }
                      }}
                      className="border-gray-400 data-[state=checked]:bg-[#722F37] data-[state=checked]:border-[#722F37]"
                      data-testid="checkbox-produtor"
                    />
                    <div>
                      <span className="font-medium" style={{ color: '#5D4037' }}>Sou Produtor</span>
                      <p className="text-xs text-gray-500 mt-0.5">Quero vender meus vinhos</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Common Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: '#5D4037' }}
                  >
                    Nome Completo *
                  </label>
                  <Input 
                    type="text"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="h-11 rounded-lg bg-white"
                    style={{ borderColor: '#D4C4B0' }}
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label 
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: '#5D4037' }}
                  >
                    E-mail *
                  </label>
                  <Input 
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="h-11 rounded-lg bg-white"
                    style={{ borderColor: '#D4C4B0' }}
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <label 
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: '#5D4037' }}
                >
                  Telefone
                </label>
                <Input 
                  type="tel"
                  placeholder="+351 912 345 678"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="h-11 rounded-lg bg-white"
                  style={{ borderColor: '#D4C4B0' }}
                  data-testid="input-phone"
                />
              </div>
              
              {/* Buyer specific fields */}
              {showBuyerFields && (
                <div className="space-y-4 pt-4 border-t border-[#E8E0D5]">
                  <h3 
                    className="text-base font-semibold"
                    style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
                  >
                    Suas Preferências
                  </h3>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Região
                    </label>
                    <Input 
                      type="text"
                      placeholder="Ex: Lisboa, Porto, Alentejo..."
                      value={formData.region}
                      onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                      className="h-11 rounded-lg bg-white"
                      style={{ borderColor: '#D4C4B0' }}
                      data-testid="input-buyer-region"
                    />
                  </div>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Tipos de Vinho Preferidos
                    </label>
                    <Input 
                      type="text"
                      placeholder="Ex: Tinto, Branco, Verde, Rosé..."
                      value={formData.preferred_wines}
                      onChange={(e) => setFormData(prev => ({ ...prev, preferred_wines: e.target.value }))}
                      className="h-11 rounded-lg bg-white"
                      style={{ borderColor: '#D4C4B0' }}
                      data-testid="input-preferred-wines"
                    />
                  </div>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Frequência de Compra
                    </label>
                    <select
                      value={formData.purchase_frequency}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchase_frequency: e.target.value }))}
                      className="w-full h-11 rounded-lg bg-white px-3 text-sm"
                      style={{ borderColor: '#D4C4B0', border: '1px solid #D4C4B0', color: '#5D4037' }}
                      data-testid="select-frequency"
                    >
                      <option value="">Selecione uma opção</option>
                      <option value="semanal">Semanalmente</option>
                      <option value="mensal">Mensalmente</option>
                      <option value="ocasional">Ocasionalmente</option>
                      <option value="eventos">Apenas para eventos</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Producer specific fields */}
              {showProducerFields && (
                <div className="space-y-4 pt-4 border-t border-[#E8E0D5]">
                  <h3 
                    className="text-base font-semibold"
                    style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
                  >
                    Informações do Produtor
                  </h3>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Nome da Empresa/Quinta
                    </label>
                    <Input 
                      type="text"
                      placeholder="Nome da sua vinícola ou quinta"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      className="h-11 rounded-lg bg-white"
                      style={{ borderColor: '#D4C4B0' }}
                      data-testid="input-company"
                    />
                  </div>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Região de Produção
                    </label>
                    <Input 
                      type="text"
                      placeholder="Ex: Douro, Alentejo, Dão..."
                      value={formData.region}
                      onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                      className="h-11 rounded-lg bg-white"
                      style={{ borderColor: '#D4C4B0' }}
                      data-testid="input-region"
                    />
                  </div>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Tipos de Vinho Produzidos
                    </label>
                    <Input 
                      type="text"
                      placeholder="Ex: Tinto, Branco, Rosé, Espumante..."
                      value={formData.wine_types}
                      onChange={(e) => setFormData(prev => ({ ...prev, wine_types: e.target.value }))}
                      className="h-11 rounded-lg bg-white"
                      style={{ borderColor: '#D4C4B0' }}
                      data-testid="input-wine-types"
                    />
                  </div>
                  
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: '#5D4037' }}
                    >
                      Capacidade de Produção Anual
                    </label>
                    <select
                      value={formData.production_capacity}
                      onChange={(e) => setFormData(prev => ({ ...prev, production_capacity: e.target.value }))}
                      className="w-full h-11 rounded-lg bg-white px-3 text-sm"
                      style={{ borderColor: '#D4C4B0', border: '1px solid #D4C4B0', color: '#5D4037' }}
                      data-testid="select-capacity"
                    >
                      <option value="">Selecione uma opção</option>
                      <option value="pequeno">Até 5.000 litros</option>
                      <option value="medio">5.000 - 20.000 litros</option>
                      <option value="grande">20.000 - 50.000 litros</option>
                      <option value="muito_grande">Mais de 50.000 litros</option>
                    </select>
                  </div>
                </div>
              )}
              
              {/* Privacy checkbox */}
              <div className="flex items-start gap-2 pt-4">
                <Checkbox 
                  id="privacy"
                  checked={formData.accepted_privacy}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accepted_privacy: checked }))}
                  className="border-gray-400 data-[state=checked]:bg-[#722F37] data-[state=checked]:border-[#722F37] mt-0.5"
                  data-testid="checkbox-privacy"
                />
                <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed" style={{ color: '#5D4037' }}>
                  Aceito a <a href="#" className="underline hover:text-[#722F37]">Política de Privacidade</a> e autorizo o tratamento dos meus dados pessoais.
                </Label>
              </div>
              
              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={loading || !formData.interest_type}
                data-testid="submit-btn"
                className="w-full py-6 text-lg font-semibold rounded-lg mt-4 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                style={{
                  backgroundColor: '#722F37',
                  color: '#F5F0E3'
                }}
              >
                {loading ? "Enviando..." : "Registrar"}
              </Button>
              
              <p className="text-xs text-center text-gray-500 mt-2">
                * Campos obrigatórios
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="pt-16 pb-8"
        style={{
          backgroundColor: '#722F37',
          borderTop: '4px solid #D4AF37'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            
            {/* Column 1 - About/Logo */}
            <div className="lg:col-span-1">
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_728153dc-96ca-4c27-a962-b3dd550d3ec5/artifacts/c8drzsa3_logo3_dark_badge-removebg-preview.png"
                  alt="O Caseirinho - Produtos Artesanais"
                  className="h-24 w-auto"
                />
              </div>
              <p 
                className="text-sm leading-relaxed mb-6"
                style={{ color: 'rgba(245, 237, 224, 0.8)' }}
              >
                Conectamos apaixonados por vinho a produtores artesanais de todo o país. Qualidade e tradição em cada garrafa.
              </p>
              {/* Social Icons */}
              <div className="flex items-center gap-3">
                <a 
                  href={content?.facebook_url || "https://facebook.com/caseirinho"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-5 h-5" style={{ color: '#D4AF37' }} />
                </a>
                <a 
                  href={content?.instagram_url || "https://instagram.com/caseirinho"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-5 h-5" style={{ color: '#D4AF37' }} />
                </a>
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Links Rápidos
              </h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={scrollToAbout}
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Sobre Nós
                  </button>
                </li>
                <li>
                  <Link 
                    to="/produtores"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Para Produtores
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/beneficios"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Benefícios
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={scrollToForm}
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Registrar
                  </button>
                </li>
                <li>
                  <a 
                    href="#"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Política de Privacidade
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Contacto
              </h4>
              <ul className="space-y-4">
                <li>
                  <a 
                    href={`mailto:${content?.contact_email || "info@caseirinho.pt"}`}
                    className="flex items-start gap-3 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                    data-testid="footer-email"
                  >
                    <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>{content?.contact_email || "info@caseirinho.pt"}</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="tel:+351912345678"
                    className="flex items-start gap-3 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>+351 912 345 678</span>
                  </a>
                </li>
                <li>
                  <div 
                    className="flex items-start gap-3 text-sm"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>Lisboa, Portugal</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Column 4 - Newsletter/CTA */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Mantenha-se Informado
              </h4>
              <p 
                className="text-sm mb-4"
                style={{ color: 'rgba(245, 237, 224, 0.8)' }}
              >
                Seja o primeiro a saber quando a plataforma estiver disponível.
              </p>
              <button 
                onClick={scrollToForm}
                className="w-full py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 hover:shadow-lg"
                style={{
                  backgroundColor: '#D4AF37',
                  color: '#722F37'
                }}
              >
                Registre-se Agora
              </button>
              
              {/* Trust badges */}
              <div className="flex items-center gap-2 mt-6">
                <Wine className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <span 
                  className="text-xs"
                  style={{ color: 'rgba(245, 237, 224, 0.6)' }}
                >
                  100% Vinhos Artesanais
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div 
            className="w-full h-[1px] mb-8"
            style={{ backgroundColor: 'rgba(212, 175, 55, 0.3)' }}
          ></div>

          {/* Bottom Footer */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p 
              className="text-sm"
              style={{ color: 'rgba(245, 237, 224, 0.6)' }}
            >
              © {new Date().getFullYear()} O Caseirinho. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Termos de Uso
              </a>
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Política de Privacidade
              </a>
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
